import { useEffect } from 'react';

/**
 * useScreenshotBlock — blocks screenshot attempts for non-super-admin users.
 *
 * Protections:
 * 1. Keyboard: blocks PrintScreen, Ctrl+Shift+S, Win+Shift+S, Cmd+Shift+3/4/5
 * 2. Right-click context menu disabled
 * 3. CSS: prevents text selection and drag
 * 4. Screen capture API detection: blurs content when tab loses visibility
 * 5. Dev tools detection: blocks Ctrl+Shift+I, F12
 */
export function useScreenshotBlock(user) {
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) return; // Super admin can screenshot freely

    // --- 1. Block screenshot keyboard shortcuts ---
    const handleKeyDown = (e) => {
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        navigator.clipboard?.writeText?.('').catch(() => {});
        document.body.style.filter = 'blur(20px)';
        setTimeout(() => { document.body.style.filter = ''; }, 1500);
        return;
      }
      // Ctrl+Shift+S (Windows snipping), Cmd+Shift+S (macOS)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        return;
      }
      // Win+Shift+S → difficult to fully block, but we can detect and blur
      // Cmd+Shift+3/4/5 (macOS screenshots)
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) {
        e.preventDefault();
        return;
      }
      // Block Ctrl+Shift+I (DevTools), F12
      if ((e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'i') || e.key === 'F12') {
        e.preventDefault();
        return;
      }
      // Block Ctrl+P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        return;
      }
    };

    // --- 2. Block right-click ---
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    // --- 3. Blur on visibility change (tab switch during screen capture) ---
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        document.body.style.filter = 'blur(20px)';
      } else {
        // Small delay before removing blur to catch fast screenshot tools
        setTimeout(() => { document.body.style.filter = ''; }, 300);
      }
    };

    // --- 4. CSS protections ---
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    // Allow text selection in input/textarea elements
    const style = document.createElement('style');
    style.id = 'screenshot-block-styles';
    style.textContent = `
      input, textarea, [contenteditable="true"] {
        -webkit-user-select: text !important;
        user-select: text !important;
      }
      @media print {
        body { display: none !important; }
      }
    `;
    document.head.appendChild(style);

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.filter = '';
      const el = document.getElementById('screenshot-block-styles');
      if (el) el.remove();
    };
  }, [isSuperAdmin]);
}
